import { useInfiniteQuery } from '@tanstack/react-query';
import {
  GitlabClient,
  GitlabRepositoriesPage,
} from '../httpClients/GitlabClient';
import { QUERY_KEYS } from './consts';

export const GITLAB_REPOSITORIES_PAGE_SIZE = 20;

/**
 * Hook to fetch repos for a specific GitLab group with infinite scroll.
 * Each group manages its own independent query — loading one group does not affect others.
 * @param {string} appConfigId - The GitLab app config ID
 * @param {number} groupId - The GitLab group ID to fetch repos for
 * @param {string} search - Search term; re-fetches from page 1 when it changes
 * @param {{ enabled?: boolean }} options - Additional query options
 * @returns {UseInfiniteQueryResult<GitlabRepositoriesPage>} Infinite query result with paginated repository pages
 */
export const useGitlabGroupRepositoriesQuery = (
  appConfigId: string,
  groupId: number,
  search: string,
  options: { enabled?: boolean } = {},
) =>
  useInfiniteQuery<GitlabRepositoriesPage>({
    queryKey: [QUERY_KEYS.GITLAB_REPOSITORIES, appConfigId, groupId, search],
    queryFn: ({ pageParam }) =>
      GitlabClient.getRepositories({
        appConfigId,
        groupId,
        page: pageParam as number,
        perPage: GITLAB_REPOSITORIES_PAGE_SIZE,
        search,
      }),
    initialPageParam: 1,
    getNextPageParam: (lastPage) =>
      lastPage.hasMore && lastPage.nextPage !== undefined
        ? lastPage.nextPage
        : undefined,
    ...options,
  });
